/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */

'use strict';

import { EntityTypes } from "../utils/dvtypes";

export class EntityLinkerModifiers {
    /**
     * Create a new Entity Linker Modifier instance
     * @param {Object} [defaults = {}] - Object to supply default values through properties
     * @constructor
     */
    constructor (defaults = {}) {
        if (defaults) {
            for (let name in defaults) {
                if (defaults.hasOwnProperty(name) && defaults[name] != null) {
                    this[name] = defaults[name];
                }
            }
        }
    }
}

/**
 * Add the specified modifier value to a property named modiferName on the specified linker. It will actually be in a propert object called "Modifiers".
 * @param {EntityLinker} linker - The Entity Linker object to modify.
 * @param {string} modifierName - The name of the property to create on the "Modifiers" object on the linker.
 * @param {*} modifierValue - The value to set to the property name of the "Modifiers" object on the linker.
 * @return {EntityLinker}
 */
function addLinkerModifier (linker, modifierName, modifierValue) {
    if (linker.Modifiers == null) {
        // if there is no "Modifiers" object on the linker, create one.
        linker.Modifiers = new EntityLinkerModifiers();
    }

    // add the specified value on the specified property of the "Modifiers" object.
    linker.Modifiers[modifierName] = modifierValue;

    // return the modified linker.
    return linker;
}

/**
 * Clear (remove) the modifier property specified. If there are no more modifiers on the "Modifiers" object, the "Modifiers" object will also be removed.
 * @param {EntityLinker} linker - The Entity Linker object to modify.
 * @param {string} modifierName - The name of the property to remove from the "Modifiers" object on the linker.
 * @return {EntityLinker}
 */
function clearLinkerModifier (linker, modifierName) {
    // if we don't have a "Modifiers" object on the linker, we are already done, eh?
    if (linker.Modifiers) {
        // remove the specified property from the "Modifiers" object.
        delete linker.Modifiers[modifierName];

        // check if there are any others...
        if (Object.getOwnPropertyNames(linker.Modifiers).length === 0) {
            // if we didn't find any other "own" properties, delete the "Modifiers" object.
            delete linker.Modifiers;
        }
    }

    // return the modified linker.
    return linker;
}

export class EntityLinker {
    /**
     * Create a Entity Linker object.
     * @param {EntityTypes|string} type - The type of entity linker to create or a string value of the type.
     * @param {string|object} UID - The entity object to create the linker for with a UID property, or the UID string.
     * @param {EntityLinkerModifiers|Object|undefined} [modifiers = null] - (OPTIONAL) If specified, create a "modifiers" object on the entity linker that is this specified object.
     * @class
     * @classdesc Entity Linker object
     */
    constructor (type, UID, modifiers = null) {
        // verify the type of linker to create.
        switch (type) {
            case "View":
            case "Property":
            case "RecordType":
            case "Style":
            case "Category":
            case "ValueStatus":
            case "Tag":
            case "PendingChange":
            case "Endpoint":
                this.Type = type;
                break;

            case EntityTypes.View:
                this.Type = "View";
                break;

            case EntityTypes.Property:
                this.Type = "Property";
                break;

            case EntityTypes.RecordType:
                this.Type = "RecordType";
                break;

            case EntityTypes.Style:
                this.Type = "Style";
                break;

            case EntityTypes.Category:
                this.Type = "Category";
                break;

            case EntityTypes.ValueStatus:
                this.Type = "ValueStatus";
                break;

            case EntityTypes.Tag:
                this.Type = "Tag";
                break;

            case EntityTypes.PendingChange:
                this.Type = "PendingChange";
                break;

            case EntityTypes.Endpoint:
                this.Type = "Endpoint";
                break;

            default:
                throw new Error("Invalid argument value for entity type.");
        }

        // verify that there is a UID property on the object.
        if (typeof UID === 'string') {
            // this is a specified string for the UID of the object.
            this.UID = UID;
        }
        else if (UID != null && typeof UID === 'object' && UID.UID != null) {
            // this is the actual object we are linking to.
            this.UID = UID.UID;
        }
        else {
            // not a valid argument!
            throw new Error("Invalid argument for entity object.");
        }

        if (modifiers) {
            /**
             * Object that contains optional modifiers for the linker
             * @type {EntityLinkerModifiers}
             */
            this.Modifiers = new EntityLinkerModifiers(modifiers);
        }
    }

    addModifier (modifier, value) {
        return addLinkerModifier(this, modifier, value);
    }

    /**
     * Remove the specified modifier named property from the linker modifier
     * @param {string} modifier - The property name of the modifier
     * @returns {EntityLinker} - This modified linker instance
     */
    removeModifier (modifier) {
        return clearLinkerModifier(this, modifier);
    }
}
